import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { ensureRouteQueryData } from "@/lib/route-loader";
import {
  getAdminSemesterGetAdminSemesterDetailQueryOptions,
  getAdminSemesterGetAdminSemestersForOrganizationQueryKey,
  useAdminSemesterDeleteSemester,
  useAdminSemesterGetAdminSemesterDetail,
  useAdminSemesterUpdateSemesterForOrganization,
} from "@/orval/admin-semester";
import { SemesterForm } from "./-components/forms/semester-form";
import type { UpsertAdminSemesterDTO } from "@/orval/model";
import { parseSemesterIdParams } from "@/lib/route-params";
import { PortalPageHeader } from "@/components/portal-page-header";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SemesterClassDaysTab } from "./-components/semester-class-days-tab";
import { useQueryClient } from "@tanstack/react-query";

const semesterSettingsSearchSchema = z.object({
  tab: z.enum(["details", "class-days", "fees", "agreements"]).catch("details"),
});

export const Route = createFileRoute("/_app/organizations/$organizationId/admin/semesters_/$semesterId")({
  parseParams: parseSemesterIdParams,
  validateSearch: semesterSettingsSearchSchema,
  loader: ({ params, ...loaderContext }) =>
    ensureRouteQueryData(
      loaderContext,
      getAdminSemesterGetAdminSemesterDetailQueryOptions(params.organizationId, params.semesterId),
    ),
  component: RouteComponent,
});

function RouteComponent() {
  const { organizationId, semesterId } = Route.useParams();
  const { tab } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: semester, isLoading } = useAdminSemesterGetAdminSemesterDetail(organizationId, semesterId);
  const { mutateAsync, isPending } = useAdminSemesterUpdateSemesterForOrganization();
  const { mutateAsync: deleteSemester } = useAdminSemesterDeleteSemester({
    mutation: { meta: { invalidates: "none" } },
  });

  const handleSubmit = async (data: UpsertAdminSemesterDTO) => {
    await mutateAsync({
      organizationId: organizationId,
      semesterId,
      data,
    });

    navigate({ from: Route.fullPath, to: ".", search: { tab } });
  };

  const handleDelete = async () => {
    if (semesterId) {
      await deleteSemester({ semesterId, organizationId: organizationId });

      await navigate({ from: Route.fullPath, to: `/organizations/$organizationId/admin/semesters` });
      await queryClient.invalidateQueries({
        queryKey: getAdminSemesterGetAdminSemestersForOrganizationQueryKey(organizationId),
      });
    }
  };

  const title = semester?.name || "Edit Semester";

  const tabLabels = { details: "Details", "class-days": "Class Days", fees: "Fees", agreements: "Agreements" } as const;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 md:p-6">
      <Tabs value={tab} onValueChange={(value) => navigate({ to: ".", search: { tab: value } })} className="w-full">
        <PortalPageHeader
          className="mb-6"
          breadcrumbs={[
            { label: "Semesters", to: "/organizations/$organizationId/admin/semesters", params: { organizationId } },
            tab === "details" ? { label: title } : { label: title, to: ".", search: { tab: "details" } },
            ...(tab === "details" ? [] : [{ label: tabLabels[tab] }]),
          ]}
          title={title}
          actions={
            <TabsList className="w-full md:w-auto">
              <TabsTrigger className="flex-1" value="details">
                Details
              </TabsTrigger>
              <TabsTrigger className="flex-1" value="class-days">
                Class Days
              </TabsTrigger>
              <TabsTrigger className="flex-1" value="fees">
                Fees
              </TabsTrigger>
              <TabsTrigger className="flex-1" value="agreements">
                Agreements
              </TabsTrigger>
            </TabsList>
          }
        />

        <TabsContent value="details">
          <SemesterForm
            organizationId={organizationId}
            initialData={semester}
            onSubmit={handleSubmit}
            onDelete={handleDelete}
            isLoading={isLoading || isPending}
            section="details"
          />
        </TabsContent>
        <TabsContent value="class-days">
          <SemesterClassDaysTab organizationId={organizationId} semesterId={semesterId} />
        </TabsContent>
        <TabsContent value="fees">
          <SemesterForm
            organizationId={organizationId}
            initialData={semester}
            onSubmit={handleSubmit}
            isLoading={isLoading || isPending}
            section="fees"
          />
        </TabsContent>
        <TabsContent value="agreements">
          <SemesterForm
            organizationId={organizationId}
            initialData={semester}
            onSubmit={handleSubmit}
            isLoading={isLoading || isPending}
            section="agreements"
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
